/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useEnableSubfolderLinksForXDP, useSubfolderLanguageAndLocale } from '@coursera-web/internationalization-lib';

import { Grid, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { Recommender } from '@coursera/event-pulse-types';

import usePrevious from 'bundles/common/hooks/usePrevious';
import { usePageConfigComponentTrackingData } from 'bundles/page-config-common/contexts/PageConfigComponentMetadataContext';
import useUserAgent from 'bundles/page/hooks/useUserAgent';
import type { CardProps } from 'bundles/product-card/components/types/ProductCardV2';
import SearchExplainHover from 'bundles/search-common/components/internal-search-explainer/SearchExplainHover';
import { ProductsCardsCdsPlaceholder } from 'bundles/search-common/components/search-cards/Placeholder';
import SearchCard from 'bundles/search-common/components/search-cards/SearchCard';
import {
  LEARNING_PRODUCTS_ENUM_MAP,
  PRODUCT_DURATIONS_STRING,
  SEARCH_MAIN_INDEX_ID,
  getTranslatedDifficulty,
  getTranslatedDurations,
} from 'bundles/search-common/constants';
import type { PRODUCT_DIFFICULTY_LEVELS } from 'bundles/search-common/constants';
import { useOwnsCourseraLite, useOwnsCourseraPlus } from 'bundles/search-common/providers/LearnerProvider';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type {
  NoResultsSearchPageEventingData,
  SearchHit,
  SearchProductHit,
  SearchUpsellHit,
} from 'bundles/search-common/providers/searchTypes';
import {
  getAvgRating,
  getHitSlug,
  getIsPathwayContent,
  getNumOfRatings,
} from 'bundles/search-common/utils/SearchUtils';
import {
  getShouldShowProductCardDensityRating,
  getShouldShowProductCardDensitySkills,
  isInFilterTestVariant,
} from 'bundles/search-common/utils/experimentUtils';
import getProductPageUrlFromHit from 'bundles/search-common/utils/getProductPageUrlFromHit';
import { getLanguageModeClarityBadges } from 'bundles/search-common/utils/searchCards';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import { isSearchProductHit, isSearchUpsellHit, scrollUpIfNecessary } from 'bundles/search-common/utils/utils';
import CourseraPlusUpsellCard from 'bundles/unified-common/components/coursera-plus-upsells/CourseraPlusUpsellCard';

import { usePremiumProductMetadata } from './usePremiumProductMetadata';

const MOBILE_CARD_IMAGE_PARAMS = { HEIGHT: 216, WIDTH: 265 };

const styles = {
  container: css`
    list-style: none;
    padding-left: 0;
  `,
  /* TrackedLink2's visibility tracking adds an extra div that interferes with the search card styling.
  This wrapper div applies style to that extra div to ensure the search cards are styled correctly.
  Note: SearchExplainHover wrapper uses display: contents, making it transparent to layout. */
  targetSearchCardWrapper: css`
    height: 100%;
    padding: 0 6px 24px;

    ${breakpoints.down('xs')} {
      padding: 0 0 24px 0;
    }

    .cds-CommonCard-previewImage > div {
      padding: 0;
      height: 100%;
    }

    .cds-CommonCard-previewImage .lazyload-wrapper {
      height: 100%;
    }

    .cds-ProductCard-list {
      /* temporaryly force list card to have border */
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    }
  `,
  upsellCardWrapper: css`
    height: 100%;
    padding: 0 6px 24px;
    ${breakpoints.between('sm', 'md')} {
      max-width: 465px;
    }
    ${breakpoints.up('sm')} {
      min-height: 450px;
    }
  `,
};

type Props = {
  hits?: Array<SearchHit | SearchUpsellHit>;
  forceOpenInNewTab?: boolean;
  noResults?: boolean;
  resultsPerPage: number;
  isSearchPage?: boolean;
  eventingData?: {
    searchIndex: string;
    searchRecommender?: Recommender;
    searchIndexPosition: number;
    page?: string;
    searchQuery?: string;
    filtersApplied?: Record<string, string[]>;
  };
  searchCardImageProps?: { fit: string };
  isLoading?: boolean;
  onScrolledToBottom?: () => void;
  totalHitsDisplayed?: number;
  cardIndexOffset?: number;
  noSearchResultsCollectionData?: NoResultsSearchPageEventingData;
  isEndOfSplitSection?: boolean;
  filterStatusTags?: (tags: string[]) => string[];
};

export const convertHitToCardProps = (
  hit: SearchProductHit,
  hitPosition: number,
  isSearchPage: boolean,
  isSEOEntityPage: boolean,
  cardIndexOffset: number,
  premiumProductDurations: Record<string, unknown> = {},
  shouldShowLanguageModeClarity: boolean,
  selectedLanguageFilters: string[],
  filterStatusTags = (tags: string[]) => [...tags]
): CardProps => {
  const hitSlug = getHitSlug(hit.url);
  let productDuration = getTranslatedDurations(PRODUCT_DURATIONS_STRING[hit.productDuration || ''] || '');
  const shouldShowProductCardDensitySkills = isSearchPage && getShouldShowProductCardDensitySkills();
  const shouldShowProductCardDensityRating = isSearchPage && getShouldShowProductCardDensityRating();
  if (hitSlug && premiumProductDurations[hitSlug] && typeof premiumProductDurations[hitSlug] === 'string') {
    productDuration = premiumProductDurations[hitSlug] as string;
  }
  const shouldTranslateProduct = isSEOEntityPage || isSearchPage;
  const baseStatusTags = hit.productCard?.badges ? filterStatusTags(hit.productCard.badges) : [];

  // Add language mode clarity badges
  const languageBadges = getLanguageModeClarityBadges(hit, shouldShowLanguageModeClarity, selectedLanguageFilters);
  const statusTags = [...baseStatusTags, ...languageBadges];
  return {
    id: hit.id || undefined,
    name: (shouldTranslateProduct && hit.translatedName) || hit.name || undefined,
    imageUrl: hit.imageUrl || undefined,
    productType: LEARNING_PRODUCTS_ENUM_MAP[hit.productType || ''],
    productDifficultyLevel: getTranslatedDifficulty(
      hit.productDifficultyLevel || ''
    ) as (typeof PRODUCT_DIFFICULTY_LEVELS)[keyof typeof PRODUCT_DIFFICULTY_LEVELS],
    productDuration: productDuration || undefined,
    partners:
      (hit.partners &&
        hit.partners.map((partner, index) => {
          return { id: index.toString(), logo: undefined, name: partner };
        })) ||
      undefined,
    partnerLogos: hit.partnerLogos || undefined,
    statusTags,
    skills: shouldShowProductCardDensitySkills
      ? undefined
      : (shouldTranslateProduct && hit.translatedSkills) || hit.skills || undefined,
    isPartOfCourseraPlus: hit.isPartOfCourseraPlus || undefined,
    isNewContent: hit.isNewContent || undefined,
    isCreditEligible: hit.isCreditEligible || undefined,
    avgProductRating: shouldShowProductCardDensityRating ? undefined : hit.avgProductRating || getAvgRating(hit),
    numProductRatings: hit.numProductRatings || getNumOfRatings(hit),
    isPathwayContent: getIsPathwayContent(hit),
    cobrandingEnabled: hit.cobrandingEnabled || undefined,
    cardPosition: cardIndexOffset + hitPosition,
    slug: hitSlug,
  };
};

const SearchCards = ({
  hits,
  forceOpenInNewTab,
  noResults = false,
  resultsPerPage,
  eventingData,
  searchCardImageProps,
  isLoading,
  isSearchPage,
  onScrolledToBottom,
  totalHitsDisplayed,
  cardIndexOffset = 0,
  noSearchResultsCollectionData,
  isEndOfSplitSection,
  filterStatusTags,
}: Props) => {
  // Fetch premium product metadata from Contentful
  const { premiumProductDurations } = usePremiumProductMetadata(hits);

  const userAgent = useUserAgent();
  const ownsCourseraLite = useOwnsCourseraLite();
  const ownsCourseraPlus = useOwnsCourseraPlus();
  const isEqp = useIsEqp();
  const isConsumerSearchPage = useIsConsumerSerp();
  const isMobileOrTabletView = useMediaQuery(breakpoints.down('md'));
  const isMobileView = useMediaQuery(breakpoints.down('xs'), { defaultMatches: userAgent?.isMobileBrowser });
  const subfolderLanguageAndLocale = useSubfolderLanguageAndLocale();
  const enableSubfolderLinksForXDP = useEnableSubfolderLinksForXDP();
  const productCardVariant = isMobileView ? 'list' : 'grid';
  const { getSearchResults, searchCardQueryParams } = useSearch();
  const { facetFilters } = getSearchResults(SEARCH_MAIN_INDEX_ID) || {};
  const shouldShowLanguageModeClarity = isConsumerSearchPage;
  const pageConfigComponentTrackingData = usePageConfigComponentTrackingData();
  // /search is the only location with infinite scroll pagination
  const isTraditionalPagination = !isSearchPage;
  const infiniteScrollTriggerRef = useRef<HTMLDivElement>(null);
  const [hasTriggeredMap, setHasTriggeredMap] = useState<Map<number, boolean>>(new Map());
  const previousHitsCount = usePrevious(totalHitsDisplayed || 0);
  const previousQuery = usePrevious(eventingData?.searchQuery || '');
  const [windowIsSafe, setWindowIsSafe] = useState(false);
  const hasScrolledBottomDetectorIndex = useMemo(() => {
    const targetTriggerIndex = (hits?.length || 0) - 1;
    const triggerShouldBeFound = !hasTriggeredMap.get(targetTriggerIndex) && !!onScrolledToBottom && !isLoading;
    return triggerShouldBeFound ? targetTriggerIndex : -1;
  }, [hits?.length, hasTriggeredMap, onScrolledToBottom, isLoading]);

  const handleScrollBottom = useCallback<([trigger]: IntersectionObserverEntry[]) => void>(
    ([trigger]: IntersectionObserverEntry[]) => {
      const shouldCallback =
        !isLoading && trigger?.isIntersecting && !hasTriggeredMap.get(hasScrolledBottomDetectorIndex);

      if (shouldCallback) {
        setHasTriggeredMap(new Map(hasTriggeredMap.set(hasScrolledBottomDetectorIndex, true)));
        onScrolledToBottom?.();
      }
    },
    [hasScrolledBottomDetectorIndex, hasTriggeredMap, isLoading, onScrolledToBottom]
  );

  const infiniteScrollObserver = useMemo(() => {
    const shouldHaveObserver = onScrolledToBottom && windowIsSafe;

    return shouldHaveObserver
      ? new IntersectionObserver(handleScrollBottom, {
          threshold: [0, 1.0],
        })
      : false;
  }, [handleScrollBottom, onScrolledToBottom, windowIsSafe]);

  useEffect(() => {
    const hasNewQuery = !!previousQuery && previousQuery !== eventingData?.searchQuery;
    // using totalHits as a proxy heuristic for when a new search has executed in order to
    // reset the infinite scroll trigger. should be cleaned up if experiment is successful.
    const displayedHitsCountShrank = (totalHitsDisplayed || 0) < (previousHitsCount || 0);
    if (displayedHitsCountShrank && !isTraditionalPagination) {
      setHasTriggeredMap(new Map());
    }

    if (hasNewQuery) {
      scrollUpIfNecessary({
        prefersReducedMotion: !!window?.matchMedia(`(prefers-reduced-motion: reduce)`)?.matches === true,
        mustBeScrolledPastThisElementId: 'main',
      });
    }

    if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      setWindowIsSafe(true);
    }

    if (infiniteScrollObserver && infiniteScrollTriggerRef.current) {
      infiniteScrollObserver.observe(infiniteScrollTriggerRef.current);
    }

    return () => {
      if (infiniteScrollObserver) infiniteScrollObserver.disconnect();
    };
  }, [
    infiniteScrollTriggerRef,
    totalHitsDisplayed,
    previousHitsCount,
    isTraditionalPagination,
    infiniteScrollObserver,
    previousQuery,
    eventingData?.searchQuery,
    isMobileOrTabletView,
  ]);
  // end experimental pagination related

  if ((isLoading && isTraditionalPagination) || !hits) {
    return <ProductsCardsCdsPlaceholder resultsPerPage={resultsPerPage} variant={productCardVariant} />;
  }

  const trackingName = noResults ? 'no_results_search_card' : 'search_card';

  const shouldUseSubfolderLinksForXDP = isEqp && enableSubfolderLinksForXDP;
  const isInFilterExptTestVar = isInFilterTestVariant() && isSearchPage;
  const desktopCardSize = isInFilterExptTestVar ? 3 : 4;

  return (
    <>
      <Grid
        container
        component="ul"
        css={[styles.container, (!isTraditionalPagination || !isEndOfSplitSection) && { marginBottom: '0' }]}
      >
        {hits.map((hit, hitIndex) => {
          if (!isSearchProductHit(hit) && !isSearchUpsellHit(hit)) return null;

          if (isSearchUpsellHit(hit)) {
            return (
              <Grid key={hit.id} item lg={desktopCardSize} md={4} sm={6} xs={12} component="li">
                <div
                  css={[styles.upsellCardWrapper]}
                  ref={hitIndex === hasScrolledBottomDetectorIndex ? infiniteScrollTriggerRef : null}
                >
                  <CourseraPlusUpsellCard
                    title={hit.title}
                    mobileBreakpoint={hit.breakpoint}
                    eventingData={{ index: hitIndex }}
                    forceOpenInNewTab={forceOpenInNewTab}
                  />
                </div>
              </Grid>
            );
          }

          const cardProps = convertHitToCardProps(
            hit,
            hitIndex,
            !!isSearchPage,
            !!isEqp,
            cardIndexOffset,
            premiumProductDurations,
            shouldShowLanguageModeClarity,
            facetFilters?.flat().filter((filter) => filter.startsWith('language:')) || [],
            filterStatusTags
          );

          return (
            <Grid key={hit.id} item lg={desktopCardSize} md={4} sm={6} xs={12} component="li">
              <SearchExplainHover hit={hit} query={eventingData?.searchQuery || ''}>
                <div
                  css={[styles.targetSearchCardWrapper]}
                  ref={hitIndex === hasScrolledBottomDetectorIndex ? infiniteScrollTriggerRef : null}
                >
                  <SearchCard
                    name={cardProps.name}
                    imageUrl={cardProps.imageUrl}
                    imageProps={
                      isMobileOrTabletView
                        ? {
                            ...searchCardImageProps,
                            height: MOBILE_CARD_IMAGE_PARAMS.HEIGHT,
                            width: MOBILE_CARD_IMAGE_PARAMS.WIDTH,
                          }
                        : searchCardImageProps
                    }
                    objectUrl={getProductPageUrlFromHit({
                      hit,
                      shouldUseSubfolderLinksForXDP,
                      subfolderLocaleCode: subfolderLanguageAndLocale,
                      queryParams: searchCardQueryParams,
                    })}
                    slug={cardProps.slug}
                    productType={cardProps.productType}
                    productDifficultyLevel={cardProps.productDifficultyLevel}
                    productDuration={cardProps.productDuration}
                    partners={cardProps.partners}
                    partnerLogos={cardProps.partnerLogos}
                    coBrandingEnabled={cardProps.cobrandingEnabled}
                    skills={cardProps.skills}
                    avgProductRatings={cardProps.avgProductRating}
                    numProductRatings={cardProps.numProductRatings}
                    statusTags={cardProps.statusTags}
                    isNewContent={cardProps.isNewContent}
                    isPartOfCourseraPlus={cardProps.isPartOfCourseraPlus}
                    ownsCourseraLite={ownsCourseraLite}
                    ownsCourseraPlus={ownsCourseraPlus}
                    cardPosition={cardProps.cardPosition}
                    shouldPrioritizeImageLoading={
                      (isMobileOrTabletView && (hitIndex ?? 10) < 2) || (!isMobileOrTabletView && (hitIndex ?? 10) < 6)
                    }
                    variant={productCardVariant}
                    trackedLinkProps={{
                      trackingName,
                      withVisibilityTracking: true,
                      requireFullyVisible: true,
                      data: {
                        objectID: hit.id || undefined,
                        hitPosition: cardIndexOffset + hitIndex,
                        queryID: eventingData?.searchQuery || '',
                        query: eventingData?.searchQuery || '',
                        listIndex: Number(eventingData?.page) || 1,
                        noResults,
                        filtersApplied: eventingData?.filtersApplied,
                        indexName: eventingData?.searchIndex,
                        indexPosition: eventingData?.searchIndexPosition,
                        ...noSearchResultsCollectionData,
                      },
                    }}
                    additionalProductCardTrackingData={
                      pageConfigComponentTrackingData && {
                        pageConfigComponent: {
                          name: pageConfigComponentTrackingData?.name ?? '',
                          index: pageConfigComponentTrackingData?.index ?? -1,
                        },
                      }
                    }
                    id={hit?.id || ''}
                    recommender={eventingData?.searchRecommender}
                    indexName={eventingData?.searchIndex}
                    searchedQuery={eventingData?.searchQuery || ''}
                    forceOpenInNewTab={forceOpenInNewTab}
                    isPathwayContent={cardProps.isPathwayContent}
                  />
                </div>
              </SearchExplainHover>
            </Grid>
          );
        })}
      </Grid>
      {isLoading && !isTraditionalPagination && (
        <ProductsCardsCdsPlaceholder resultsPerPage={resultsPerPage} variant={productCardVariant} />
      )}
    </>
  );
};

export default memo(SearchCards);
